import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_HOME } from "@/lib/roles";

/**
 * Guards a route by auth and (optionally) allowed roles.
 *
 * Usage:
 *   <ProtectedRoute allow={[ROLES.ADMIN]}> <AdminLayout /> </ProtectedRoute>
 *
 * State machine (hang-proof — a review requirement, not a nicety):
 *   loading                      → spinner (short, watchdog-released)
 *   session && profileLoading    → spinner (post-sign-in hydration)
 *   session && !profile          → retry screen (never an eternal spinner)
 *   !session                     → /login
 */
export default function ProtectedRoute({ allow, children }) {
  const {
    isAuthenticated,
    loading,
    role,
    session,
    profile,
    profileLoading,
    retryProfile,
    signOut,
  } = useAuth();
  const location = useLocation();

  if (loading || (session && !profile && profileLoading)) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-screen flex items-center justify-center bg-background"
      >
        <div className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  // Signed in but the profile could not be loaded (offline, slow
  // network, transient server error). Give the user real controls
  // instead of a dead end.
  if (session && !profile) {
    return (
      <div
        data-testid="auth-profile-error"
        className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-8 text-center"
      >
        <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
          We couldn't load your account
        </p>
        <p className="max-w-xs text-sm leading-relaxed text-neutral-600">
          Please check your connection and try again.
        </p>
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            data-testid="auth-profile-retry"
            onClick={() => retryProfile?.()}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-hover active:scale-[0.98]"
          >
            Try again
          </button>
          <button
            type="button"
            data-testid="auth-profile-signout"
            onClick={() => signOut?.()}
            className="rounded-full bg-neutral-100 px-5 py-2.5 text-sm font-semibold text-neutral-700 transition-all hover:bg-neutral-200 active:scale-[0.98]"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (allow && allow.length && !allow.includes(role)) {
    const home = ROLE_HOME[role] || "/unauthorized";
    return <Navigate to={home} replace />;
  }

  return children;
}
